<template>
  <div
    :class="{
      'comment-input-group-small': size === 'small'
    }"
  >
    <div class="comment-input-group">
      <a-space
        v-if="rows === 1"
        :size="4"
      >
        <Upload
          :button="{ size }"
          @files-uploaded="onAttachmentUploaded"
        >
          <template #icon>
            <CloudUploadOutlined />
          </template>
        </Upload>
        <MentionDialog
          v-model:content="content"
          v-model:mentionedUserIds="mentionedUserIds"
          :size="size"
        />
        <a-popover
          trigger="click"
          overlay-class-name="emoji-dialog"
        >
          <template #content>
            <EmojiMart @select="onSelectEmoji" />
          </template>
          <a-button :size="size">
            <template #icon>
              <SmileOutlined />
            </template>
          </a-button>
        </a-popover>
      </a-space>
      <MentionTextarea
        v-model:value="content"
        v-model:mentioned-user-ids="mentionedUserIds"
        :rows="rows"
        :placeholder="placeholder"
        class="comment-textarea"
        :class="{ 'multi-row-textarea': rows !== 1 }"
        @blur="onBlur"
      />
      <a-button
        v-if="rows === 1"
        type="primary"
        :size="size"
        :disabled="!content && attachments.length === 0"
        @click="sendComment"
      >
        {{ $t('okr.comment.send') }}
      </a-button>
    </div>
    <div
      v-if="rows !== 1"
      class="toolbar"
    >
      <a-space :size="4">
        <Upload
          :button="{ size }"
          @files-uploaded="onAttachmentUploaded"
        >
          <template #icon>
            <CloudUploadOutlined />
          </template>
        </Upload>
        <MentionDialog
          v-model:content="content"
          v-model:mentionedUserIds="mentionedUserIds"
          :size="size"
        />
        <a-popover
          trigger="click"
          overlay-class-name="emoji-dialog"
        >
          <template #content>
            <EmojiMart @select="onSelectEmoji" />
          </template>
          <a-button :size="size">
            <template #icon>
              <SmileOutlined />
            </template>
          </a-button>
        </a-popover>
      </a-space>
      <a-button
        type="primary"
        :size="size"
        :disabled="!content && attachments.length === 0"
        @click="sendComment"
      >
        {{ $t('okr.comment.send') }}
      </a-button>
    </div>

    <FileList
      :can-edit="true"
      :files="attachments"
      class="attachments"
      @file-deleted="onDeleteAttachment"
    />
  </div>
</template>

<script>
import map from 'lodash/map'
import { ref } from 'vue'
import { CloudUploadOutlined, SmileOutlined } from '@ant-design/icons-vue'
import { useI18n } from 'vue-i18n'
import { message } from 'ant-design-vue'

import { replaceStr2Html } from '@/utils/line-break'
import { createObjectiveComment, createKeyResultComment, createOkrListReview } from '@/api/comment'
import useMention from '@/composables/use-mention'
import EmojiMart from '@/components/EmojiMart'
import Upload from '@/components/Upload'
import FileList from '@/components/FileList'
import MentionTextarea from '@/components/MentionTextarea'
import MentionDialog from './MentionDialog'

export default {
  name: 'CommentInputGroup',
  components: {
    CloudUploadOutlined,
    SmileOutlined,
    EmojiMart,
    Upload,
    FileList,
    MentionTextarea,
    MentionDialog
  },
  props: {
    entity: {
      type: Object,
      required: true
    },
    replyingComment: {
      type: Object,
      default: () => ({})
    },
    placeholder: {
      type: String,
      required: true
    },
    size: {
      type: String,
      default: 'small'
    },
    rows: {
      type: Number,
      default: 1
    }
  },
  emits: ['sent'],
  setup (props, { emit }) {
    const { t } = useI18n()
    const { getUserIds } = useMention()
    const content = ref('')
    const mentionedUserIds = ref([])
    const attachments = ref([])

    let createComment
    switch (props.entity.type) {
      case 'objective':
        createComment = createObjectiveComment
        break
      case 'keyResult':
        createComment = createKeyResultComment
        break
      case 'okrList':
        createComment = createOkrListReview
        break
    }

    let className = 'sendComment'
    let successMessage = t('okr.comment.sentMessage')

    if (props.entity.type === 'okrList') {
      className = 'sendMyselfComment'
      successMessage = t('okr.okrListReview.sentMessage')
    }

    function reset () {
      content.value = ''
      mentionedUserIds.value = []
      attachments.value = []
    }

    function sendComment () {
      createComment(props.entity.id, {
        content: `<div class=${className}>${replaceStr2Html(content.value)}</div>`,
        mentionedUserIds: mentionedUserIds.value,
        attachmentIds: map(attachments.value, 'id'),
        ...props.replyingComment
      }).then(() => {
        reset()
        message.success(successMessage)
        emit('sent')
      })
    }

    function onAttachmentUploaded (uploadedFiles) {
      const uploadedAttachments = uploadedFiles.map(file => ({
        ...file,
        name: file.filename
      }))
      attachments.value.push(...uploadedAttachments)
    }

    function onDeleteAttachment (id) {
      attachments.value = attachments.value.filter(attachment => attachment.id !== id)
    }

    function onSelectEmoji (emoji) {
      content.value += emoji
    }

    function onBlur () {
      mentionedUserIds.value = getUserIds(content.value, mentionedUserIds.value)
    }

    return {
      content,
      mentionedUserIds,
      attachments,
      sendComment,
      onAttachmentUploaded,
      onDeleteAttachment,
      onSelectEmoji,
      onBlur
    }
  }
}
</script>

<style lang="less" scoped>
.comment-input-group {
  display: flex;
  align-items: flex-start;
}

.comment-textarea {
  margin: 0 4px;
  flex: 1;
}

.comment-input-group-small {
  :deep(.ant-mentions) {
    textarea {
      font-size: 14px;
      min-height: 20px;
      padding: 3px 8px;
      line-height: 16px;
    }
  }
}

.attachments {
  margin-top: 4px;
}

.toolbar {
  margin-top: 8px;
  display: flex;
  justify-content: space-between;
}

.multi-row-textarea {
  margin: 0;
}
</style>
